import React from 'react';
import { FiEdit2, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { useColorContext } from '../../../context/colorContext';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const createGradient = (color, amount, fallback) => {
  const safeColor = toSafeHex(color, fallback);
  const r = Math.min(255, parseInt(safeColor.slice(1, 3), 16) + amount);
  const g = Math.min(255, parseInt(safeColor.slice(3, 5), 16) + amount);
  const b = Math.min(255, parseInt(safeColor.slice(5, 7), 16) + amount);
  const lighterColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  return `linear-gradient(135deg, ${safeColor} 0%, ${lighterColor} 100%)`;
};

const BUTTON_ANIM_STYLE_ID = 'plynote-button-shadow-anim';
if (typeof document !== 'undefined' && !document.getElementById(BUTTON_ANIM_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = BUTTON_ANIM_STYLE_ID;
  style.textContent = `
    @keyframes outlineShadowPulse {
      0% {
        box-shadow: 0 6px 18px var(--outline-shadow-soft), inset 0 1px 0 var(--outline-highlight);
      }
      50% {
        box-shadow: 0 12px 28px var(--outline-shadow-strong), inset 0 1px 0 var(--outline-highlight);
      }
      100% {
        box-shadow: 0 6px 18px var(--outline-shadow-soft), inset 0 1px 0 var(--outline-highlight);
      }
    }
  `;
  document.head.appendChild(style);
}

const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md',
  color,
  disabled = false,
  className = '',
  ...props 
}) => {
  const {
    bgPrimary,
    bgSecondary,
    textPrimary,
    textSecondary,
    activeAccent,
    first_background_color,
    second_background_color,
    first_vark_color,
    first_text_color,
    second_text_color,
  } = useColorContext();

  // Compatibility: support legacy keys and current ColorContext keys.
  const resolvedBgPrimary = toSafeHex(bgPrimary || first_background_color, '#0A0A0A');
  const resolvedBgSecondary = toSafeHex(bgSecondary || second_background_color, '#191919');
  const resolvedTextPrimary = toSafeHex(textPrimary || first_text_color, '#F0F0F0');
  const resolvedTextSecondary = toSafeHex(textSecondary || second_text_color, '#BDBDBD');
  const defaultColor = toSafeHex(activeAccent || first_vark_color, '#1976d2');
  const buttonColor = toSafeHex(color, defaultColor);

  // Base styles for all buttons
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter, sans-serif',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    opacity: disabled ? '0.5' : '1',
    outline: 'none',
  };

  // Size variants
  const sizes = {
    sm: {
      padding: '8px 16px',
      fontSize: '14px',
    },
    md: {
      padding: '12px 24px',
      fontSize: '16px',
    },
    lg: {
      padding: '16px 32px',
      fontSize: '18px',
    },
  };

  // Style variants
  const variants = {
    primary: {
      background: createGradient(buttonColor, 30, defaultColor),
      color: resolvedTextPrimary,
      boxShadow: `0 4px 15px ${hexToRgba(buttonColor, 0.3, defaultColor)}`,
    },
    secondary: {
      background: resolvedBgPrimary,
      color: resolvedTextPrimary,
      border: `1px solid ${hexToRgba(resolvedTextSecondary, 0.35, resolvedTextSecondary)}`,
      boxShadow: `0 2px 8px ${hexToRgba(resolvedBgSecondary, 0.35, resolvedBgSecondary)}`,
    },
    outline: {
      background: `linear-gradient(135deg, ${hexToRgba(buttonColor, 0.78, defaultColor)} 0%, ${hexToRgba(buttonColor, 0.62, defaultColor)} 100%)`,
      color: resolvedTextPrimary,
      border: `1px solid ${hexToRgba(buttonColor, 0.9, defaultColor)}`,
      boxShadow: `0 6px 18px ${hexToRgba(buttonColor, 0.32, defaultColor)}, inset 0 1px 0 ${hexToRgba(resolvedTextPrimary, 0.18, resolvedTextPrimary)}`,
      animation: 'outlineShadowPulse 2.4s ease-in-out infinite',
    },
  };

  // Combine all styles
  const buttonStyle = {
    ...baseStyles,
    ...sizes[size],
    ...variants[variant],
    '--outline-shadow-soft': hexToRgba(buttonColor, 0.32, defaultColor),
    '--outline-shadow-strong': hexToRgba(buttonColor, 0.42, defaultColor),
    '--outline-highlight': hexToRgba(resolvedTextPrimary, 0.18, resolvedTextPrimary),
  };

  // Hover effect handler
  const handleMouseEnter = (e) => {
    const target = e.currentTarget;
    if (!disabled) {
      if (variant === 'primary') {
        target.style.transform = 'translateY(-2px) scale(1.0)';
        target.style.boxShadow = `0 6px 20px ${hexToRgba(buttonColor, 0.5, defaultColor)}`;
      } else if (variant === 'secondary') {
        target.style.background = hexToRgba(resolvedBgPrimary, 0.86, resolvedBgPrimary);
        target.style.transform = 'translateY(-2px)';
        target.style.boxShadow = `0 4px 12px ${hexToRgba(resolvedBgSecondary, 0.45, resolvedBgSecondary)}`;
      } else if (variant === 'outline') {
        target.style.background = `linear-gradient(135deg, ${hexToRgba(buttonColor, 0.9, defaultColor)} 0%, ${hexToRgba(buttonColor, 0.74, defaultColor)} 100%)`;
        target.style.transform = 'translateY(-2px)';
        target.style.boxShadow = `0 10px 24px ${hexToRgba(buttonColor, 0.42, defaultColor)}, inset 0 1px 0 ${hexToRgba(resolvedTextPrimary, 0.24, resolvedTextPrimary)}`;
      }
    }
  };

  const handleMouseLeave = (e) => {
    const target = e.currentTarget;
    if (!disabled) {
      target.style.transform = 'translateY(0) scale(1)';
      target.style.boxShadow = variants[variant].boxShadow || 'none';
      if (variant === 'secondary') {
        target.style.background = resolvedBgPrimary;
      } else if (variant === 'outline') {
        target.style.background = variants.outline.background;
      }
    }
  };

  const handleMouseDown = (e) => {
    const target = e.currentTarget;
    if (!disabled) {
      if (variant === 'primary') {
        target.style.filter = 'brightness(0.85)';
        target.style.transform = 'translateY(-2px) scale(1.0)';
      } else if (variant === 'secondary') {
        target.style.background = hexToRgba(resolvedBgPrimary, 0.72, resolvedBgPrimary);
        target.style.transform = 'translateY(-2px)';
      } else if (variant === 'outline') {
        target.style.background = `linear-gradient(135deg, ${hexToRgba(buttonColor, 0.98, defaultColor)} 0%, ${hexToRgba(buttonColor, 0.8, defaultColor)} 100%)`;
        target.style.borderColor = hexToRgba(buttonColor, 1, defaultColor);
        target.style.transform = 'translateY(-1px)';
      }
    }
  };

  const handleMouseUp = (e) => {
    const target = e.currentTarget;
    if (!disabled) {
      if (variant === 'primary') {
        target.style.filter = 'brightness(1)';
        target.style.transform = 'translateY(-2px) scale(1.0)';
      } else if (variant === 'secondary') {
        target.style.background = hexToRgba(resolvedBgPrimary, 0.86, resolvedBgPrimary);
        target.style.transform = 'translateY(-2px)';
      } else if (variant === 'outline') {
        target.style.background = `linear-gradient(135deg, ${hexToRgba(buttonColor, 0.9, defaultColor)} 0%, ${hexToRgba(buttonColor, 0.74, defaultColor)} 100%)`;
        target.style.borderColor = hexToRgba(buttonColor, 0.95, defaultColor);
        target.style.transform = 'translateY(-2px)';
      }
    }
  };

  return (
    <button
      style={buttonStyle}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={disabled}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
// CreateNoteButton Component - Special button for creating new notes
const CreateNoteButton = ({ 
  onClick, 
  size = 'md',
  color,
  disabled = false,
  iconOnly = false,
  className = '',
  ...props 
}) => {
  const {
    first_background_color,
    second_background_color,
    first_vark_color,
    first_text_color,
    second_text_color,
  } = useColorContext();

  const defaultColor = first_vark_color;
  const noteColor = color || defaultColor;

  // Size configs
  const sizeConfig = {
    sm: {
      padding: '10px 14px',
      fontSize: '14px',
      pencilSize: 24,
    },
    md: {
      padding: '14px 18px',
      fontSize: '16px',
      pencilSize: 32,
    },
    lg: {
      padding: '18px 22px',
      fontSize: '18px',
      pencilSize: 40,
    },
  };

  const config = sizeConfig[size];

  const [isHovered, setIsHovered] = React.useState(false);

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    padding: iconOnly ? '0' : config.padding,
    fontSize: config.fontSize,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    background: iconOnly ? 'transparent' : first_background_color,
    boxShadow: iconOnly ? 'none' : `0 0 20px ${hexToRgba(noteColor, 0.1, first_vark_color)}`,
    opacity: disabled ? '0.5' : '1',
    outline: 'none',
    background: isHovered ? hexToRgba(noteColor, 0.15, first_vark_color) : first_background_color,
    color: first_text_color,
    boxShadow: isHovered ? `0 6px 20px ${hexToRgba(noteColor, 0.3, first_vark_color)}` : `0 2px 8px ${hexToRgba(second_background_color, 0.32, second_background_color)}`,
  };

  const handleMouseEnter = () => {
    if (!disabled) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Animated Pencil Icon with Square
  const AnimatedPencil = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: config.pencilSize,
        height: config.pencilSize,
        border: `2px solid ${isHovered ? noteColor : second_text_color}`,
        borderRadius: '6px',
        transition: 'border-color 0.3s ease',
      }}
    >
      <FiEdit2
        size={config.pencilSize * 0.6}
        color={isHovered ? noteColor : second_text_color}
        style={{
          transition: 'color 0.3s ease',
          strokeWidth: 2,
        }}
      />
    </div>
  );

  return (
    <button
      style={{
        ...baseStyles,
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      className={className}
      {...props}
    >
      <AnimatedPencil />
    </button>
  );
};

// AudioPlayButton Component - Audio play/pause button with smooth animations
const AudioPlayButton = ({ 
  isPlaying = false,
  onClick, 
  size = 'md',
  color,
  disableShadow = false,
  disabled = false,
  className = '',
  ...props 
}) => {
  const {
    bgPrimary,
    bgSecondary,
    activeAccent,
    textPrimary,
    second_background_color,
    first_background_color,
    first_text_color,
    first_vark_color,
  } = useColorContext();

  const resolvedBgPrimary = toSafeHex(bgPrimary || first_background_color, '#0A0A0A');
  const resolvedSecondary = toSafeHex(bgSecondary || second_background_color, '#191919');
  const resolvedTextPrimary = toSafeHex(textPrimary || first_text_color, '#F0F0F0');
  const defaultColor = toSafeHex(activeAccent || first_vark_color, '#3B82F6');
  const accentColor = toSafeHex(color, defaultColor);

  // Size configs
  const sizeConfig = {
    sm: {
      width: '30px',
      height: '30px',
      iconSize: 18,
    },
    md: {
      width: '36px',
      height: '36px',
      iconSize: 24,
    },
    lg: {
      width: '42px',
      height: '42px',
      iconSize: 28,
    },
  };

  const config = sizeConfig[size];

  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!disabled) {
      if (onClick) onClick(e);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === ' ' || e.key === 'Enter') && !disabled) {
      e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      if (onClick) onClick(e);
    }
  };

  const handleMouseEnter = () => {
    if (!disabled) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: config.width,
    height: config.height,
    borderRadius: '50%',
    border: `1px solid ${hexToRgba(accentColor, isHovered ? 0.5 : 0.3, defaultColor)}`,
    fontFamily: 'Inter, sans-serif',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: disabled ? '0.5' : '1',
    outline: 'none',
    background: isHovered ? hexToRgba(accentColor, 0.14, defaultColor) : resolvedBgPrimary,
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'none',
  };

  const iconFill = isHovered
    ? accentColor
    : resolvedTextPrimary;

  const PremiumPlayPauseIcon = () => (
    <svg
      width={config.iconSize}
      height={config.iconSize}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transition: 'all 0.2s ease',
        transform: isPlaying ? 'scale(0.98)' : 'scale(1)',
      }}
    >
      <rect
        x="7"
        y="4"
        width="3"
        height="16"
        rx="1"
        fill={iconFill}
        style={{
          opacity: isPlaying ? 1 : 0,
          transform: `translateX(${isPlaying ? '0px' : '2px'}) scaleY(${isPlaying ? 1 : 0.25})`,
          transformOrigin: 'center',
          transition: 'all 0.2s ease',
        }}
      />

      <rect
        x="14"
        y="4"
        width="3"
        height="16"
        rx="1"
        fill={iconFill}
        style={{
          opacity: isPlaying ? 1 : 0,
          transform: `translateX(${isPlaying ? '0px' : '-2px'}) scaleY(${isPlaying ? 1 : 0.25})`,
          transformOrigin: 'center',
          transition: 'all 0.2s ease',
        }}
      />

      <path
        d="M7 5.5L18 12L7 18.5V5.5Z"
        fill={iconFill}
        style={{
          opacity: isPlaying ? 0 : 1,
          transform: `translateX(${isPlaying ? '-3px' : '0px'}) scale(${isPlaying ? 0.72 : 1})`,
          transformOrigin: 'center',
          transition: 'all 0.2s ease',
        }}
      />
    </svg>
  );

  return (
    <button
      type="button"
      style={{
        ...baseStyles,
        transform: 'scale(1)',
        boxShadow: disableShadow
          ? 'none'
          : (isHovered
            ? `0 6px 18px ${hexToRgba(accentColor, 0.28, defaultColor)}`
            : `0 2px 8px ${hexToRgba(resolvedSecondary, 0.3, resolvedSecondary)}`),
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      className={className}
      aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
      aria-pressed={isPlaying}
      {...props}
    >
      <PremiumPlayPauseIcon />
    </button>
  );
};

const ReduceExpandButton = ({
  status = 'expand',
  onClick,
  size = 'md',
  color,
  disabled = false,
  className = '',
  ...props
}) => {
  const {
    first_background_color,
    second_background_color,
    first_vark_color,
  } = useColorContext();

  const defaultColor = first_vark_color;
  const accentColor = color || defaultColor;

  const sizeConfig = {
    sm: { width: 32, height: 32, strokeWidth: 2.2 },
    md: { width: 38, height: 38, strokeWidth: 2.4 },
    lg: { width: 44, height: 44, strokeWidth: 2.6 },
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = (event) => {
    if (disabled) return;
    onClick?.(event);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(event);
    }
  };

  const strokeColor = isHovered ? accentColor : hexToRgba(accentColor, 0.8);
  const isReduce = status === 'reduce';

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      className={className}
      aria-label={isReduce ? 'Reduce view' : 'Expand view'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${config.width}px`,
        height: `${config.height}px`,
        borderRadius: '999px',
        border: `1px solid ${hexToRgba(accentColor, isHovered ? 0.45 : 0.28, first_vark_color)}`,
        background: isHovered ? hexToRgba(accentColor, 0.14, first_vark_color) : first_background_color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
        boxShadow: isHovered
          ? `0 4px 14px ${hexToRgba(accentColor, 0.3, first_vark_color)}`
          : `0 2px 8px ${hexToRgba(second_background_color, 0.34, second_background_color)}`,
        outline: 'none',
      }}
      {...props}
    >
      <svg
        width="65%"
        height="65%"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        {isReduce ? (
          <>
            <path
              d="M3.5 9.5L9 9.5L9 4"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20.5 14.5L15 14.5L15 20"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 9.5L11 11.5"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
            />
            <path
              d="M15 14.5L13 12.5"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
            />
          </>
        ) : (
          <>
            <path
              d="M11 11.5L9 9.5"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
            />
            <path
              d="M13 12.5L15 14.5"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
            />
            <path
              d="M9 9.5L3.5 9.5L3.5 4"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 14.5L20.5 14.5L20.5 20"
              stroke={strokeColor}
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </svg>
    </button>
  );
};

const FullscreenToggleButton = ({
  isFullscreen = false,
  onToggle,
  size = 'md',
  color,
  width,
  height,
  iconSize,
  position = 'top-right',
  offset = 14,
  top,
  right,
  bottom,
  left,
  zIndex = 1000,
  borderRadius = '10px',
  backgroundColor,
  hoverBackgroundColor,
  iconColor,
  hoverIconColor,
  borderColor,
  hoverBorderColor,
  boxShadow,
  hoverBoxShadow,
  hoverScale = 1.05,
  transition = 'all 0.2s ease, transform 0.25s ease',
  iconTransition = 'transform 0.25s ease, opacity 0.2s ease',
  fullscreenIconScale = 0.95,
  disabled = false,
  className = '',
  style,
  ...props
}) => {
  const {
    bgPrimary,
    bgSecondary,
    textPrimary,
    textSecondary,
    activeAccent,
    first_background_color,
    second_background_color,
    first_vark_color,
    first_text_color,
  } = useColorContext();

  const resolvedBgPrimary = toSafeHex(bgPrimary || first_background_color, '#0A0A0A');
  const resolvedBgSecondary = toSafeHex(bgSecondary || second_background_color, '#191919');
  const resolvedTextPrimary = toSafeHex(textPrimary || first_text_color, '#F0F0F0');
  const resolvedTextSecondary = toSafeHex(textSecondary || first_text_color, '#BDBDBD');
  const defaultColor = toSafeHex(activeAccent || first_vark_color, '#1976d2');
  const accentColor = toSafeHex(color, defaultColor);

  const sizeConfig = {
    sm: { width: 30, height: 30, iconSize: 16 },
    md: { width: 36, height: 36, iconSize: 18 },
    lg: { width: 42, height: 42, iconSize: 20 },
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const resolvedWidth = width ?? config.width;
  const resolvedHeight = height ?? config.height;
  const resolvedIconSize = iconSize ?? config.iconSize;
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = (event) => {
    if (disabled) return;
    onToggle?.(event);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle?.(event);
    }
  };

  const Icon = isFullscreen ? FiMinimize2 : FiMaximize2;
  const resolvedPosition = {
    'top-right': { top: top ?? offset, right: right ?? offset, bottom: undefined, left: undefined },
    'top-left': { top: top ?? offset, left: left ?? offset, bottom: undefined, right: undefined },
    'bottom-right': { bottom: bottom ?? offset, right: right ?? offset, top: undefined, left: undefined },
    'bottom-left': { bottom: bottom ?? offset, left: left ?? offset, top: undefined, right: undefined },
    custom: { top, right, bottom, left },
  }[position] || { top: top ?? offset, right: right ?? offset };

  const resolvedBackgroundColor = backgroundColor || resolvedBgPrimary;
  const resolvedIconColor = iconColor || resolvedTextPrimary;
  const resolvedBoxShadow = boxShadow || `0 2px 8px ${hexToRgba(resolvedBgSecondary, 0.34, resolvedBgSecondary)}`;
  const resolvedBorderColor = borderColor || hexToRgba(accentColor, 0.3, defaultColor);
  const resolvedHoverBorderColor = hoverBorderColor || hexToRgba(accentColor, 0.5, defaultColor);
  const resolvedHoverBackgroundColor = hoverBackgroundColor || hexToRgba(accentColor, 0.14, defaultColor);
  const resolvedHoverIconColor = hoverIconColor || accentColor;
  const resolvedHoverBoxShadow = hoverBoxShadow || `0 6px 18px ${hexToRgba(accentColor, 0.28, defaultColor)}`;

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      className={className}
      style={{
        position: 'absolute',
        top: resolvedPosition.top,
        right: resolvedPosition.right,
        bottom: resolvedPosition.bottom,
        left: resolvedPosition.left,
        zIndex,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: typeof resolvedWidth === 'number' ? `${resolvedWidth}px` : resolvedWidth,
        height: typeof resolvedHeight === 'number' ? `${resolvedHeight}px` : resolvedHeight,
        borderRadius,
        border: `1px solid ${isHovered ? resolvedHoverBorderColor : resolvedBorderColor}`,
        background: isHovered ? resolvedHoverBackgroundColor : resolvedBackgroundColor,
        color: isHovered ? resolvedHoverIconColor : resolvedIconColor,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
        transition,
        transform: isHovered ? `scale(${hoverScale})` : 'scale(1)',
        boxShadow: isHovered ? resolvedHoverBoxShadow : resolvedBoxShadow,
        ...style,
      }}
      {...props}
    >
      <Icon
        size={resolvedIconSize}
        style={{
          transition: iconTransition,
          transform: isFullscreen ? `scale(${fullscreenIconScale})` : 'scale(1)',
        }}
      />
    </button>
  );
};

const PlusButton = ({
  onClick,
  size = 'md',
  color,
  disabled = false,
  className = '',
  style,
  ...props
}) => {
  const {
    bgPrimary,
    bgSecondary,
    textPrimary,
    activeAccent,
    first_background_color,
    second_background_color,
    first_vark_color,
    first_text_color,
  } = useColorContext();

  const resolvedBgPrimary = toSafeHex(bgPrimary || first_background_color, '#0A0A0A');
  const resolvedBgSecondary = toSafeHex(bgSecondary || second_background_color, '#191919');
  const resolvedTextPrimary = toSafeHex(textPrimary || first_text_color, '#F0F0F0');
  const defaultColor = toSafeHex(activeAccent || first_vark_color, '#1976d2');
  const accentColor = toSafeHex(color, defaultColor);

  const sizeConfig = {
    sm: { width: 30, height: 30, iconSize: 16, strokeWidth: 2.2 },
    md: { width: 36, height: 36, iconSize: 18, strokeWidth: 2.4 },
    lg: { width: 42, height: 42, iconSize: 20, strokeWidth: 2.6 },
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const [isHovered, setIsHovered] = React.useState(false);

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(event);
    }
  };

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      aria-label="Add"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${config.width}px`,
        height: `${config.height}px`,
        borderRadius: '999px',
        border: `1px solid ${hexToRgba(accentColor, isHovered ? 0.5 : 0.3, defaultColor)}`,
        background: isHovered ? hexToRgba(accentColor, 0.14, defaultColor) : resolvedBgPrimary,
        color: isHovered ? accentColor : resolvedTextPrimary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
        transition: 'all 0.2s ease',
        boxShadow: isHovered
          ? `0 6px 18px ${hexToRgba(accentColor, 0.28, defaultColor)}`
          : `0 2px 8px ${hexToRgba(resolvedBgSecondary, 0.3, resolvedBgSecondary)}`,
        ...style,
      }}
      {...props}
    >
      <svg
        width={config.iconSize}
        height={config.iconSize}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 5V19"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />
        <path
          d="M5 12H19"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
};

const MinusButton = ({
  onClick,
  size = 'md',
  color,
  disabled = false,
  className = '',
  style,
  ...props
}) => {
  const {
    bgPrimary,
    bgSecondary,
    textPrimary,
    activeAccent,
    first_background_color,
    second_background_color,
    first_vark_color,
    first_text_color,
  } = useColorContext();

  const resolvedBgPrimary = toSafeHex(bgPrimary || first_background_color, '#0A0A0A');
  const resolvedBgSecondary = toSafeHex(bgSecondary || second_background_color, '#191919');
  const resolvedTextPrimary = toSafeHex(textPrimary || first_text_color, '#F0F0F0');
  const defaultColor = toSafeHex(activeAccent || first_vark_color, '#1976d2');
  const accentColor = toSafeHex(color, defaultColor);

  const sizeConfig = {
    sm: { width: 30, height: 30, iconSize: 16, strokeWidth: 2.2 },
    md: { width: 36, height: 36, iconSize: 18, strokeWidth: 2.4 },
    lg: { width: 42, height: 42, iconSize: 20, strokeWidth: 2.6 },
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const [isHovered, setIsHovered] = React.useState(false);

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(event);
    }
  };

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      aria-label="Remove"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${config.width}px`,
        height: `${config.height}px`,
        borderRadius: '999px',
        border: `1px solid ${hexToRgba(accentColor, isHovered ? 0.5 : 0.3, defaultColor)}`,
        background: isHovered ? hexToRgba(accentColor, 0.14, defaultColor) : resolvedBgPrimary,
        color: isHovered ? accentColor : resolvedTextPrimary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
        transition: 'all 0.2s ease',
        boxShadow: isHovered
          ? `0 6px 18px ${hexToRgba(accentColor, 0.28, defaultColor)}`
          : `0 2px 8px ${hexToRgba(resolvedBgSecondary, 0.3, resolvedBgSecondary)}`,
        ...style,
      }}
      {...props}
    >
      <svg
        width={config.iconSize}
        height={config.iconSize}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 12H19"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
};

const CenterMapButton = ({
  onClick,
  size = 'md',
  color,
  disabled = false,
  className = '',
  style,
  ...props
}) => {
  const {
    bgPrimary,
    bgSecondary,
    textPrimary,
    activeAccent,
    first_background_color,
    second_background_color,
    first_vark_color,
    first_text_color,
  } = useColorContext();

  const resolvedBgPrimary = toSafeHex(bgPrimary || first_background_color, '#0A0A0A');
  const resolvedBgSecondary = toSafeHex(bgSecondary || second_background_color, '#191919');
  const resolvedTextPrimary = toSafeHex(textPrimary || first_text_color, '#F0F0F0');
  const defaultColor = toSafeHex(activeAccent || first_vark_color, '#1976d2');
  const accentColor = toSafeHex(color, defaultColor);

  const sizeConfig = {
    sm: { width: 30, height: 30, iconSize: 16, strokeWidth: 2.0 },
    md: { width: 36, height: 36, iconSize: 18, strokeWidth: 2.0 },
    lg: { width: 42, height: 42, iconSize: 20, strokeWidth: 2.2 },
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const [isHovered, setIsHovered] = React.useState(false);

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(event);
    }
  };

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      aria-label="Center map"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${config.width}px`,
        height: `${config.height}px`,
        borderRadius: '999px',
        border: `1px solid ${hexToRgba(accentColor, isHovered ? 0.5 : 0.3, defaultColor)}`,
        background: isHovered ? hexToRgba(accentColor, 0.14, defaultColor) : resolvedBgPrimary,
        color: isHovered ? accentColor : resolvedTextPrimary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
        transition: 'all 0.2s ease',
        boxShadow: isHovered
          ? `0 6px 18px ${hexToRgba(accentColor, 0.28, defaultColor)}`
          : `0 2px 8px ${hexToRgba(resolvedBgSecondary, 0.3, resolvedBgSecondary)}`,
        ...style,
      }}
      {...props}
    >
      <svg
        width={config.iconSize}
        height={config.iconSize}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth={config.strokeWidth} />
        <path d="M12 3V7" stroke="currentColor" strokeWidth={config.strokeWidth} strokeLinecap="round" />
        <path d="M12 17V21" stroke="currentColor" strokeWidth={config.strokeWidth} strokeLinecap="round" />
        <path d="M3 12H7" stroke="currentColor" strokeWidth={config.strokeWidth} strokeLinecap="round" />
        <path d="M17 12H21" stroke="currentColor" strokeWidth={config.strokeWidth} strokeLinecap="round" />
      </svg>
    </button>
  );
};

const ImportMaterialButton = ({
  mainText = 'Import',
  secondaryText = 'Add a document, a link or media',
  icon = null,
  iconContainerStyle = {},
  iconStyle = {},
  iconGap = 12,
  iconColor,
  iconColorHover,
  iconSize = 18,
  onClick,
  disabled = false,
  className = '',
  style = {},
  mainTextStyle = {},
  secondaryTextStyle = {},
  width = '100%',
  minHeight = 72,
  padding = '12px 14px',
  borderRadius = 12,
  textAlign = 'left',
  gap = 4,
  fontFamily = 'Inter, sans-serif',
  background,
  backgroundHover,
  textColor,
  textColorHover,
  secondaryTextColor,
  secondaryTextColorHover,
  outlineColor,
  outlineColorHover,
  outlineWidth = 1,
  outlineWidthHover = 2,
  boxShadow,
  boxShadowHover,
  transition = 'background-color 260ms cubic-bezier(0.22, 1, 0.36, 1), color 240ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 320ms cubic-bezier(0.22, 1, 0.36, 1), transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
  mainTextSize = 14,
  secondaryTextSize = 12,
  mainTextWeight = 600,
  secondaryTextWeight = 500,
  uppercaseMainText = false,
  letterSpacingMain = '0.01em',
  letterSpacingSecondary = '0.01em',
  ...props
}) => {
  const {
    first_background_color,
    second_background_color,
    first_vark_color,
    second_vark_color,
    first_text_color,
    second_text_color,
  } = useColorContext();

  const resolvedBackground = background || first_background_color;
  const resolvedBackgroundHover = backgroundHover || hexToRgba(first_background_color, 0.92, first_background_color);
  const resolvedTextColor = textColor || first_text_color;
  const resolvedTextColorHover = textColorHover || first_text_color;
  const resolvedSecondaryTextColor = secondaryTextColor || second_text_color;
  const resolvedSecondaryTextColorHover = secondaryTextColorHover || first_text_color;
  const resolvedOutlineColor = outlineColor || hexToRgba(second_text_color, 0.45, second_text_color);
  const resolvedOutlineColorHover = outlineColorHover || second_vark_color;
  const resolvedBoxShadow = boxShadow || `0 2px 10px ${hexToRgba(second_background_color, 0.34, second_background_color)}`;
  const resolvedBoxShadowHover = boxShadowHover || `0 10px 24px ${hexToRgba(first_vark_color, 0.24, first_vark_color)}`;
  const resolvedIconColor = iconColor || second_text_color;
  const resolvedIconColorHover = iconColorHover || first_text_color;

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const isActive = !disabled && (isHovered || isFocused);
  const hasIcon = React.isValidElement(icon);
  const ringWidth = isActive ? outlineWidthHover : outlineWidth;
  const ringColor = isActive ? resolvedOutlineColorHover : resolvedOutlineColor;
  const layeredShadow = `${isActive ? resolvedBoxShadowHover : resolvedBoxShadow}, inset 0 0 0 ${ringWidth}px ${ringColor}`;

  const rootStyle = {
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: textAlign === 'center' ? 'center' : 'flex-start',
    width,
    minHeight,
    padding,
    border: 'none',
    borderRadius,
    backgroundColor: isActive ? resolvedBackgroundHover : resolvedBackground,
    outline: 'none',
    color: isActive ? resolvedTextColorHover : resolvedTextColor,
    boxShadow: layeredShadow,
    transition,
    transform: 'translateY(0)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    textAlign,
    fontFamily,
    gap,
    lineHeight: 1.25,
    willChange: 'background-color, box-shadow, color, transform',
    ...style,
  };

  const topTextStyle = {
    display: 'block',
    fontSize: mainTextSize,
    fontWeight: mainTextWeight,
    letterSpacing: letterSpacingMain,
    color: isActive ? resolvedTextColorHover : resolvedTextColor,
    textTransform: uppercaseMainText ? 'uppercase' : 'none',
    transition: 'color 180ms ease',
    ...mainTextStyle,
  };

  const bottomTextStyle = {
    display: 'block',
    fontSize: secondaryTextSize,
    fontWeight: secondaryTextWeight,
    letterSpacing: letterSpacingSecondary,
    color: isActive ? resolvedSecondaryTextColorHover : resolvedSecondaryTextColor,
    transition: 'color 180ms ease',
    ...secondaryTextStyle,
  };

  const contentRowStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: hasIcon ? 'space-between' : (textAlign === 'center' ? 'center' : 'flex-start'),
    gap: hasIcon ? iconGap : 0,
  };

  const textColumnStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: textAlign === 'center' ? 'center' : 'flex-start',
    gap,
    minWidth: 0,
    flex: hasIcon ? 1 : 'unset',
  };

  const iconWrapperStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isActive ? resolvedIconColorHover : resolvedIconColor,
    flexShrink: 0,
    transition: 'color 180ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease',
    transform: 'translateX(0)',
    opacity: disabled ? 0.7 : 1,
    ...iconContainerStyle,
  };

  const renderedIcon = hasIcon
    ? React.cloneElement(icon, {
      size: icon.props?.size || iconSize,
      style: {
        display: 'block',
        ...(icon.props?.style || {}),
        ...iconStyle,
      },
      color: icon.props?.color || undefined,
    })
    : null;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={className}
      style={rootStyle}
      onMouseEnter={() => {
        if (!disabled) setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => {
        if (!disabled) setIsFocused(true);
      }}
      onBlur={() => setIsFocused(false)}
      {...props}
    >
      <span style={contentRowStyle}>
        <span style={textColumnStyle}>
          <span style={topTextStyle}>{mainText}</span>
          <span style={bottomTextStyle}>{secondaryText}</span>
        </span>
        {hasIcon ? <span style={iconWrapperStyle}>{renderedIcon}</span> : null}
      </span>
    </button>
  );
};

const import_material_button = (props) => <ImportMaterialButton {...props} />;

export {
  Button,
  CreateNoteButton,
  AudioPlayButton,
  ReduceExpandButton,
  FullscreenToggleButton,
  PlusButton,
  MinusButton,
  CenterMapButton,
  ImportMaterialButton,
  import_material_button,
};